/**
 * Sui address validation for the send form.
 *
 * A Sui address is a 32-byte value rendered as "0x" followed by 64 hex
 * characters. The reference clients normalize to lowercase, so this accepts
 * lowercase hex and rejects mixed or upper case to keep the displayed address
 * canonical. The gateway builds and broadcasts the Sui transaction server-side
 * from the plain destination and decimal amount, so this module validates the
 * typed address only and produces no payload.
 */

/** Sui amounts are denominated in MIST, with nine decimal places to one SUI. */
export const SUI_DECIMALS = 9;

/** A 0x-prefixed 32-byte address as 64 lowercase hex characters. */
const SUI_ADDRESS = /^0x[0-9a-f]{64}$/;

/**
 * Validate a Sui address. Returns an empty string when valid, or a short reason
 * when not, matching the form's per-field error convention.
 *
 * @param address The address as entered.
 * @returns An error message, or an empty string when valid.
 */
export function suiAddressError(address: string): string {
  const value = typeof address === 'string' ? address.trim() : '';
  if (value === '') return 'Enter a recipient address.';
  if (!value.startsWith('0x')) return 'A Sui address starts with 0x.';
  if (!SUI_ADDRESS.test(value)) {
    return 'A Sui address is 0x followed by 64 lowercase hex characters.';
  }
  return '';
}

/**
 * Whether a Sui address is well formed.
 *
 * @param address The address as entered.
 * @returns True when the address validates.
 */
export function isValidSuiAddress(address: string): boolean {
  return suiAddressError(address) === '';
}
